/**
 * Warstwa API modułu: G — ścieżka kariery.
 * Właściciel: FALA 1, agent G-1 (G-3/G-4 dopisują swoje) (docs/PROJEKT-00-FUNDAMENT-2026-09-02.md §5, §6).
 * Trasy backendu: api/career, api/hr-config/competencies.
 *
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js (interceptory JWT, cache GET, obsługa 401)
 * i zwraca OBIEKT nazwanych metod, które apiService.js doczepia do wspólnego `apiService` (jedno miejsce,
 * src/api/modules/index.js). Nazwy metod muszą być unikalne w całej aplikacji — kolizja zatrzymuje start
 * (patrz attachApiModules). Tego pliku nie dotyka nikt poza właścicielem modułu.
 *
 * UWAGA na „me": własną kartę rozwoju pobiera się aliasem `me`, nie własnym identyfikatorem. Dzięki temu
 * strona pracownika działa, zanim wczyta się profil, a odpowiedź nigdy nie zależy od tego, co przeglądarka
 * wpisze w adres — o zakresie rozstrzyga wyłącznie serwer (ICareerPathService).
 */
export function attach(api) {
    return {
        // ── Karta jednej osoby ───────────────────────────────────────────
        getCareerOverview: (employeeId = "me") => api.get(`/career/${employeeId}/overview`),
        getCareerTimeline: (employeeId = "me", limit = 200) =>
            api.get(`/career/${employeeId}/timeline`, { params: { limit } }),
        getCareerCompetencies: (employeeId = "me") => api.get(`/career/${employeeId}/competencies`),
        getCareerAchievements: (employeeId = "me") => api.get(`/career/${employeeId}/achievements`),
        getCareerTrainings: (employeeId = "me") => api.get(`/career/${employeeId}/trainings`),

        // ── Akcje przełożonego / kadr ────────────────────────────────────
        assessCareerCompetencies: (employeeId, pozycje) =>
            api.post(`/career/${employeeId}/competencies`, pozycje),
        selfAssessCareerCompetencies: (pozycje, employeeId = "me") =>
            api.post(`/career/${employeeId}/competencies/self`, pozycje),

        /**
         * [OBSZAR 2 2026-09-17] Potwierdzenie samooceny pracownika przez przełożonego / kadry.
         *
         * Wysyłamy WYŁĄCZNIE listę kompetencji (pusta = wszystkie czekające na odpowiedź) i opcjonalny
         * komentarz — POZIOM bierze serwer z bieżącego `SelfLevel`. Gdyby liczbę przysyłał ekran,
         * potwierdzenie dotyczyłoby stanu sprzed ostatniej zmiany pracownika.
         */
        acceptCareerSelfAssessment: (employeeId, competencyIds = [], note = null) =>
            api.post(`/career/${employeeId}/competencies/accept-self`, { competencyIds, note }),
        addCareerEvent: (employeeId, wpis) => api.post(`/career/${employeeId}/events`, wpis),
        deleteCareerEvent: (eventId) => api.delete(`/career/events/${eventId}`),

        /**
         * Decyzja przełożonego o osiągnięciu. Świadomie idzie ISTNIEJĄCĄ trasą silnika wniosków
         * (`/hr-requests/{id}/advance-step`), a nie własnym endpointem modułu: osiągnięcie JEST wnioskiem
         * HR, więc drugi endpoint decyzji oznaczałby drugą ścieżkę akceptacji — dokładnie ten dubel,
         * przed którym ostrzega historia repo (dwa silniki ścieżki).
         * @param {"approve"|"reject"} action
         */
        decideCareerAchievement: (requestId, action, comment = "") =>
            api.post(`/hr-requests/${requestId}/advance-step`, { action, comment }),

        // ── Zespół ───────────────────────────────────────────────────────
        getCareerTeam: (scope = "team") => api.get("/career/team", { params: { scope } }),
        getCareerPendingAchievements: () => api.get("/career/team/pending-achievements"),

        // ── Słowniki ─────────────────────────────────────────────────────
        getCareerCompetencyCatalog: () => api.get("/career/competency-catalog"),
        getCareerDictionaries: () => api.get("/career/dictionaries"),

        // ── Szkolenia, certyfikaty i kursy (G-3) ───────────────────
        planCareerTraining: (employeeId, dane) => api.post(`/career/${employeeId}/trainings/plan`, dane),
        completeCareerTraining: (employeeId, trainingId, dane = {}) =>
            api.patch(`/career/${employeeId}/trainings/${trainingId}/complete`, dane),
        deleteCareerTraining: (employeeId, trainingId) =>
            api.delete(`/career/${employeeId}/trainings/${trainingId}`),

        getCareerEnrollments: (employeeId = "me") => api.get(`/career/${employeeId}/elearning`),
        getCareerAssignableCourses: (employeeId) => api.get(`/career/${employeeId}/elearning/available`),
        assignCareerCourse: (employeeId, dane) => api.post(`/career/${employeeId}/elearning/assign`, dane),

        // ── Dokumenty wydane i skany certyfikatów (G-4) ────────────
        //
        // Wykaz dokumentów — same metadane. BAJTY pobiera się wyłącznie przez rejestr wydań
        // (`getDocumentIssuePdf`), gdzie stoi bramka po rodzaju źródła, kontrola hasha i dziennik otwarć.
        getCareerIssuedDocuments: (employeeId = "me") => api.get(`/career/${employeeId}/documents`),

        /**
         * Skan certyfikatu z rejestru szkoleń. Ta trasa istnieje dla PRZEŁOŻONEGO bez klucza e-teczki:
         * wydaje wyłącznie pliki wskazane przez wpis rejestru szkoleń tej osoby, tym samym czytnikiem
         * i z tym samym wpisem w dzienniku, co podgląd w aktach.
         */
        getCareerTrainingFile: (employeeId, fileId, pobierz = false) =>
            api.get(`/career/${employeeId}/files/${fileId}`, {
                params: { download: pobierz },
                responseType: "blob",
            }),

        /** Wydanie certyfikatu kursu w rejestrze — { issueId, available, message }. */
        getELearningCertificateIssue: (courseId) => api.get(`/elearning/${courseId}/certificate-issue`),

        // ── Profile stanowisk i luka kompetencyjna (G etap 5) ──────
        //
        // Luka = wymagany poziom ze profilu stanowiska MINUS poziom nadany. Liczy ją serwer,
        // front wyłącznie rysuje — inaczej kafelek w nagłówku i kolumna w tabeli mogłyby podać
        // dwie różne liczby dla tej samej osoby.
        getCareerCompetencyGap: (employeeId = "me") => api.get(`/career/${employeeId}/competency-gap`),

        getCareerPositionProfiles: () => api.get("/career/position-profiles"),
        getCareerPositionOptions: () => api.get("/career/position-profiles/options"),
        createCareerPositionRequirement: (dto) => api.post("/career/position-profiles", dto),
        updateCareerPositionRequirement: (id, dto) => api.put(`/career/position-profiles/${id}`, dto),
        deleteCareerPositionRequirement: (id) => api.delete(`/career/position-profiles/${id}`),

        // ── PLAN ROZWOJU (obszar E, 2026-09-03) ────────────────────
        //
        // JEDNO wywołanie na całą zakładkę. Poprzednia wersja składała „plan" w przeglądarce z pięciu
        // odpowiedzi pobranych na inne potrzeby — przez to kolejność „co pilne" zależała od tego,
        // która odpowiedź wróciła pierwsza, a licznik w nagłówku bywał inny niż lista pod nim.
        getCareerPlan: (employeeId = "me") => api.get(`/career/${employeeId}/plan`),
        saveCareerPlan: (dane, employeeId = "me") => api.put(`/career/${employeeId}/plan`, dane),
        submitCareerPlan: (employeeId = "me") => api.post(`/career/${employeeId}/plan/submit`),
        reviewCareerPlan: (employeeId, dane) => api.post(`/career/${employeeId}/plan/review`, dane),

        /** Stanowiska z opisanym profilem kompetencji — podpowiedź celu planu (sama lista nazw). */
        getCareerPlanTargets: () => api.get("/career/plan/targets"),

        // ── Dokumenty z AKT OSOBOWYCH (odpowiedź na „mam dokumenty ze szkoleń, a nie ma ich tutaj") ──
        //
        // Sam WYKAZ. Plik otwiera się istniejącą drogą akt — razem z jej bramką wydania i wpisem
        // w dzienniku dostępu. Drugi czytnik plików akt oznaczałby drugą bramkę do pominięcia.
        getCareerPersonalFileDocs: (employeeId = "me") => api.get(`/career/${employeeId}/personal-file`),
        getCareerPersonalFilePreview: (fileId, pobierz = false) =>
            api.get(`/eteczka/files/${fileId}/${pobierz ? "download" : "preview"}`, { responseType: "blob" }),

        // ── Redakcja słownika (/admin/hr-config → Kompetencje, bramka HrAccess) ──
        getHrCompetencyCatalog: () => api.get("/hr-config/competencies"),
        createHrCompetency: (dto) => api.post("/hr-config/competencies", dto),
        updateHrCompetency: (id, dto) => api.put(`/hr-config/competencies/${id}`, dto),
        deactivateHrCompetency: (id) => api.delete(`/hr-config/competencies/${id}`),
    };
}
